import { useEffect, useMemo, useRef, useState } from "react";
import { loadGoogleMaps } from "@/lib/gmaps";
import { MapPin } from "lucide-react";

/**
 * Full-route map view for the Driver app. Renders every stop on the current
 * route as a numbered, color-coded pin (view-only — pins are not clickable).
 *
 *   completed    → green
 *   cart_not_out → amber
 *   missed       → red
 *   pending      → blue (default)
 *
 * The pin number reflects the stop's position in the current route order.
 * Stops without lat/lng are silently skipped (map still renders the rest).
 * The map auto-fits its bounds to the visible stops on first render and
 * whenever the stop set materially changes.
 */
const STATUS_COLOR = {
    completed: "#10b981",     // emerald-500
    cart_not_out: "#f59e0b",  // amber-500
    missed: "#ef4444",        // red-500
    pending: "#2563eb",       // blue-600
};

function pinSvg(color, number) {
    // Small teardrop pin with the sequence number rendered inside the head.
    // Encoded as a data-URI SVG so Google Maps can consume it directly.
    const label = String(number);
    const fontSize = label.length > 2 ? 10 : 12;
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="34" height="46" viewBox="0 0 34 46">
  <path d="M17 1.5c-8.28 0-15 6.5-15 14.5 0 10.9 13.4 27 14.1 27.9a1.2 1.2 0 0 0 1.8 0C18.6 43 32 26.9 32 16 32 8 25.28 1.5 17 1.5z"
        fill="${color}" stroke="#ffffff" stroke-width="2" />
  <circle cx="17" cy="16" r="9" fill="#ffffff" fill-opacity="0.95" />
  <text x="17" y="16" text-anchor="middle" dominant-baseline="central"
        font-family="ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif"
        font-size="${fontSize}" font-weight="700" fill="${color}">${label}</text>
</svg>`.trim();
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export default function RouteStopsMap({ stops = [], className = "", testid = "route-stops-map" }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const [error, setError] = useState(false);

    // Only stops we can plot. Preserve original index so the pin number matches
    // the driver's numbered list order.
    const plottable = useMemo(
        () => stops
            .map((s, i) => ({ stop: s, seq: i + 1 }))
            .filter(({ stop }) => stop && stop.lat != null && stop.lng != null),
        [stops],
    );

    // A cheap "signature" so we only rebuild markers when the stops actually
    // change (id, order, status, or coords) — not on every parent re-render.
    const signature = useMemo(
        () => plottable
            .map(({ stop, seq }) => `${stop.id}:${seq}:${stop.status}:${stop.lat},${stop.lng}`)
            .join("|"),
        [plottable],
    );

    useEffect(() => {
        let cancelled = false;
        if (plottable.length === 0) return;
        loadGoogleMaps().then((google) => {
            if (cancelled || !containerRef.current) return;
            if (!mapRef.current) {
                mapRef.current = new google.maps.Map(containerRef.current, {
                    center: { lat: plottable[0].stop.lat, lng: plottable[0].stop.lng },
                    zoom: 12,
                    disableDefaultUI: true,
                    zoomControl: true,
                    gestureHandling: "greedy",
                    clickableIcons: false,
                    styles: [
                        { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
                        { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
                    ],
                });
            }

            // Wipe old markers and redraw. Route sizes are small enough
            // (< 300 stops per truck / day) that this stays cheap.
            markersRef.current.forEach((m) => m.setMap(null));
            markersRef.current = [];

            const bounds = new google.maps.LatLngBounds();
            plottable.forEach(({ stop, seq }) => {
                const color = STATUS_COLOR[stop.status] || STATUS_COLOR.pending;
                const marker = new google.maps.Marker({
                    position: { lat: stop.lat, lng: stop.lng },
                    map: mapRef.current,
                    icon: {
                        url: pinSvg(color, seq),
                        scaledSize: new google.maps.Size(34, 46),
                        anchor: new google.maps.Point(17, 44),
                    },
                    title: `${seq}. ${stop.customer_name || ""}`.trim(),
                    clickable: false,
                    optimized: true,
                });
                markersRef.current.push(marker);
                bounds.extend({ lat: stop.lat, lng: stop.lng });
            });

            if (plottable.length === 1) {
                mapRef.current.setCenter({ lat: plottable[0].stop.lat, lng: plottable[0].stop.lng });
                mapRef.current.setZoom(15);
            } else {
                mapRef.current.fitBounds(bounds, 48);
            }
        }).catch(() => setError(true));
        return () => { cancelled = true; };
        // signature intentionally captures the meaningful shape of `plottable`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    if (error) {
        return (
            <div className={`flex items-center justify-center text-center text-xs text-muted-foreground bg-secondary/30 ${className}`}
                 data-testid={`${testid}-fallback`}>
                <div className="space-y-1">
                    <MapPin className="w-5 h-5 mx-auto opacity-50" />
                    <div>Map unavailable</div>
                </div>
            </div>
        );
    }

    if (plottable.length === 0) {
        return (
            <div className={`flex items-center justify-center text-center text-xs text-muted-foreground bg-secondary/30 ${className}`}
                 data-testid={`${testid}-empty`}>
                <div className="space-y-1">
                    <MapPin className="w-5 h-5 mx-auto opacity-50" />
                    <div>No stops with coordinates yet</div>
                </div>
            </div>
        );
    }

    return <div ref={containerRef} className={className} data-testid={testid} />;
}
